/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { Label, RequiredAsterisk } from '@atlaskit/form';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

const DEFAULT_SCALE_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const getSafeId = (value: string): string => value.replace(/[^A-Za-z0-9_-]/g, '-');

const styles = cssMap({
	container: {
		overflowX: 'auto',
		backgroundImage: `linear-gradient(to right, ${token('elevation.surface')}, ${token('elevation.surface')}), linear-gradient(to left, ${token('elevation.surface')}, ${token('elevation.surface')}), linear-gradient(to right, ${token('elevation.shadow.overflow.spread')}, transparent), linear-gradient(to left, ${token('elevation.shadow.overflow.spread')}, transparent)`,
		backgroundPosition: 'left center, right center, left center, right center',
		backgroundRepeat: 'no-repeat',
		backgroundSize: `${token('space.100')} 100%, ${token('space.100')} 100%, ${token('space.100')} 100%, ${token('space.100')} 100%`,
		backgroundAttachment: 'local, local, scroll, scroll',
	},
	options: {
		width: 'fit-content',
		minWidth: '100%',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
	buttonWrapper: {
		flex: 1,
	},
});

type Props = {
	label: string;
	name: string;
	id: string;
	isRequired: boolean;
	options?: string[];
};

export const ScaleInput = ({ label, name, id, isRequired, options = [] }: Props): JSX.Element => {
	const scaleOptions = options.length > 0 ? options : DEFAULT_SCALE_OPTIONS;
	const fieldId = getSafeId(id);
	const labelId = `${fieldId}-label`;

	const [selected, setSelected] = useState<string | null>(null);

	return (
		<Stack space="space.050">
			<Label id={labelId} htmlFor={fieldId}>
				{label}
				{isRequired && <RequiredAsterisk />}
			</Label>
			<Box
				id={fieldId}
				xcss={styles.container}
				testId="survey-builder-scale-input"
				role="radiogroup"
				aria-labelledby={labelId}
				aria-required={isRequired}
			>
				<Box xcss={styles.options}>
					<Inline space="space.200">
						{scaleOptions.map((option, index) => {
							const position = String(index + 1);
							const inputId = `${fieldId}-${getSafeId(position)}`;
							const isChecked = selected === position;

							return (
								<Box key={inputId} xcss={styles.buttonWrapper}>
									<VisuallyHidden>
										<Radio
											id={inputId}
											value={position}
											label={position}
											name={name}
											isRequired={isRequired}
											isChecked={isChecked}
											onChange={() => setSelected(position)}
										/>
									</VisuallyHidden>
									<Button
										appearance="default"
										shouldFitContainer
										isSelected={isChecked}
										onClick={() => setSelected(position)}
										aria-hidden
										tabIndex={-1}
										testId={`survey-builder-scale-input-option-${position}`}
									>
										{position}
									</Button>
								</Box>
							);
						})}
					</Inline>
				</Box>
			</Box>
		</Stack>
	);
};
